import React, { useState, useEffect } from 'react';
import { ChevronDown, Wand2, AlertCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getAllPairs, pairKey, pairRole, defaultExcludedPairKeys, PAIR_ROLE_LABELS } from '../generators/tieFormats';

const GENDER_OPTIONS = [
  { value: 'male', label: 'M' },
  { value: 'female', label: 'F' },
  { value: null, label: '—' },
];

/**
 * Seeds local pending edit state for every team from its current override
 * layered on top of the roster's own gender data — so a player whose gender
 * is already known (from mock data, or real profile data later) shows as
 * already set in the toggle, not blank, even before the organizer has
 * touched anything. An explicit override still wins over the roster value.
 */
const initPending = (teams, overrides) =>
  Object.fromEntries(
    teams.map((team) => {
      const existing = overrides[team.id];
      const rosterGenders = Object.fromEntries(team.roster.filter((p) => p.gender).map((p) => [p.id, p.gender]));
      const genders = { ...rosterGenders, ...(existing?.genders || {}) };
      const excludedPairKeys = existing?.excludedPairKeys || defaultExcludedPairKeys(team.roster, genders);
      return [team.id, { genders, excludedPairKeys }];
    })
  );

const GenderToggle = ({ value, onChange, theme }) => (
  <div className={`inline-flex rounded-full border ${theme.border} p-0.5`}>
    {GENDER_OPTIONS.map((opt) => (
      <button
        key={String(opt.value)}
        type="button"
        onClick={() => onChange(opt.value)}
        className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold transition-colors ${
          value === opt.value ? theme.buttonPrimary : `${theme.textMuted} hover:${theme.text}`
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const TeamCard = ({ team, pending, onChange, theme, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const pairs = getAllPairs(team.roster);
  const excludedCount = pending.excludedPairKeys.length;
  const valid = excludedCount === 2;

  // Both handlers pass an UPDATER function rather than a precomputed next
  // value — `pending` here is a snapshot from this render, and two clicks
  // fired close enough together (before a re-render lands) would otherwise
  // both compute their `next` off that same stale snapshot, so the second
  // click's update silently overwrites the first's instead of stacking.
  // Deriving from the latest state at update time (see updateTeam below)
  // closes that race regardless of click timing.
  const setGender = (playerId, gender) => {
    onChange(team.id, (prev) => {
      const genders = { ...prev.genders };
      if (gender) genders[playerId] = gender;
      else delete genders[playerId];
      return { ...prev, genders };
    });
  };

  const togglePair = (key) => {
    onChange(team.id, (prev) => {
      const isExcluded = prev.excludedPairKeys.includes(key);
      const excludedPairKeys = isExcluded ? prev.excludedPairKeys.filter((k) => k !== key) : [...prev.excludedPairKeys, key];
      return { ...prev, excludedPairKeys };
    });
  };

  return (
    <div className={`rounded-xl border ${theme.border} ${theme.card}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3.5 py-2.5 text-left"
      >
        <span className={`text-sm font-semibold ${theme.text}`}>{team.name}</span>
        <span className="flex items-center gap-2">
          {!valid && (
            <span className={`flex items-center gap-1 text-[10px] font-medium text-amber-500`}>
              <AlertCircle size={11} />
              {excludedCount}/2 sitting out
            </span>
          )}
          <ChevronDown size={15} className={`${theme.textMuted} transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className={`space-y-3 border-t ${theme.border} px-3.5 py-3`}>
          <div>
            <div className={`mb-1.5 text-[10px] font-semibold uppercase tracking-wide ${theme.textMuted}`}>
              Gender (optional — refines the Men's/Women's/Mixed labels below)
            </div>
            <div className="space-y-1.5">
              {team.roster.map((player) => (
                <div key={player.id} className="flex items-center justify-between gap-2">
                  <span className={`text-xs ${theme.text}`}>{player.name}</span>
                  <GenderToggle value={pending.genders[player.id] || null} onChange={(g) => setGender(player.id, g)} theme={theme} />
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className={`mb-1.5 text-[10px] font-semibold uppercase tracking-wide ${theme.textMuted}`}>
              Doubles pairs — pick exactly 2 of the 6 to sit out this tournament
            </div>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {pairs.map((pair) => {
                const key = pairKey(pair);
                const excluded = pending.excludedPairKeys.includes(key);
                const role = pairRole(pair, pending.genders);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => togglePair(key)}
                    className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-left text-xs transition-colors ${
                      excluded ? `${theme.border} ${theme.backgroundSecondary} opacity-50` : `border-transparent ${theme.tabActive}`
                    }`}
                  >
                    <span className={excluded ? theme.textMuted : ''}>
                      {pair[0].name.split(' ')[0]} + {pair[1].name.split(' ')[0]}
                    </span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide ${excluded ? theme.textMuted : ''}`}>
                        {PAIR_ROLE_LABELS[role]}
                      </span>
                      {excluded && <span className="text-[10px] font-bold uppercase tracking-wide text-amber-500">Sitting out</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * MLP-only configuration panel: per team, optionally set each player's
 * gender (refines the Men's/Women's/Mixed labels, but doubles play works
 * fine without it) and pick which 2 of the roster's 6 possible doubles pairs
 * sit out — the other 4 play across the tie's 4 doubles games. Edits are
 * local until "Apply" — mirrors the explicit regenerate step the organizer
 * asked for, rather than recomputing on every click.
 */
const TeamPairingConfig = ({ teams, overrides, onApply }) => {
  const { theme } = useTheme();
  const [pendingByTeam, setPendingByTeam] = useState(() => initPending(teams, overrides));

  // Re-seed whenever the underlying team list changes (a new schedule was
  // generated) — but NOT on every `overrides` change, so an in-progress
  // edit isn't wiped out by the Apply that edit itself just triggered.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPendingByTeam(initPending(teams, overrides)), [teams]);

  // `next` is an updater function (see setGender/togglePair above) — resolved
  // against `prev[teamId]` inside the same functional setState call, so it
  // always reads the truly-latest per-team state, never a stale render snapshot.
  const updateTeam = (teamId, next) =>
    setPendingByTeam((prev) => ({ ...prev, [teamId]: next(prev[teamId]) }));

  // `pendingByTeam` can momentarily lag `teams` (render happens before the
  // reseed effect above runs, e.g. right after a new schedule is generated
  // with a different team set) — fall back to an empty list rather than
  // reading `.length` off `undefined`.
  const allValid = teams.every((t) => (pendingByTeam[t.id]?.excludedPairKeys || []).length === 2);

  return (
    <div className={`mb-4 rounded-xl border ${theme.border} ${theme.backgroundSecondary} p-4`}>
      <div className={`mb-3 text-sm font-semibold ${theme.text}`}>Configure MLP pairings</div>
      <div className="space-y-2">
        {teams.map((team, i) => (
          <TeamCard
            key={team.id}
            team={team}
            pending={pendingByTeam[team.id] || { genders: {}, excludedPairKeys: [] }}
            onChange={updateTeam}
            theme={theme}
            defaultOpen={i === 0 && teams.length === 1}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => onApply(pendingByTeam)}
        disabled={!allValid}
        className={`mt-3 flex w-full items-center justify-center gap-2 rounded-lg py-2 text-xs font-semibold transition-all ${
          allValid ? `${theme.buttonPrimary} hover:opacity-90` : `${theme.buttonGhost} cursor-not-allowed opacity-50`
        }`}
      >
        <Wand2 size={13} />
        Apply &amp; regenerate slate
      </button>
      {!allValid && (
        <p className={`mt-1.5 text-center text-[11px] ${theme.textMuted}`}>
          Each team needs exactly 2 pairs sitting out before applying.
        </p>
      )}
    </div>
  );
};

export default TeamPairingConfig;
